import { useEffect, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  StatCard,
  Textarea,
  Tabs,
  Toast,
} from '@/components/ui';
import ClientActivity from '@/pages/admin/ClientActivity';
import ClientAiSettings from '@/pages/admin/ClientAiSettings';
import { formatMoney, formatNumber, formatPercent } from '@/lib/format';
import {
  CampaignDispositions,
  type CampaignDispositionsData,
} from '@/components/CampaignDispositions';
import { VicidialServers } from '@/components/VicidialServers';
import { ClientLicense } from '@/components/ClientLicense';
import { ClientPortalAccess } from '@/components/ClientPortalAccess';

interface AdminBoard {
  id: string;
  name: string;
  code: string;
  tenant_id: string;
  service: string | null;
  clips: number;
  playable: boolean;
}

interface ClientDetailResponse {
  client: {
    id: string;
    name: string;
    slug: string;
    status: string;
    billing_email: string | null;
    contact_name: string | null;
    contact_phone: string | null;
    timezone: string;
    currency: string;
    price_multiplier: string;
    expose_provider_costs: boolean;
    max_concurrent_calls: number;
    plan_id: string | null;
    plan_name: string | null;
    offerings: string[] | null;
    max_bots: number;
    max_client_users: number;
    max_vicidial_servers: number;
    max_vicidial_users: number;
    billing_model: string;
    mrc_per_bot: string;
    per_call_price: string;
    included_calls: number;
    setup_fee: string;
    recording_enabled: boolean;
    enforce_calling_hours: boolean;
    recording_retention_days: number;
    ai_company_name: string | null;
    ai_company_brand: string | null;
    max_call_seconds: number | null;
    features: Record<string, unknown> | null;
  };
  stats: {
    bots: number;
    bot_groups: number;
    users: number;
    vicidial_users: number;
    vicidial_servers?: number;
    assignments: number;
    calls: number;
  };
  margin_30d: {
    actual_cost: number;
    client_price: number;
    gross_margin: number;
    minutes: number;
  };
  /** What the client is actually charged and what it costs us (licence-aware). */
  economics_30d?: {
    billing_mode: string;
    licence_revenue: number;
    usage_revenue: number;
    revenue: number;
    provider_cost: number;
    profit: number;
    margin_pct: number | null;
  };
  campaign_dispositions: CampaignDispositionsData;
}

interface Plan {
  id: string;
  name: string;
  code: string;
}

// Mirrors app/bot_engine/modes.py. Short enough to restate; the server is
// still the authority, and it refuses anything not in its own list.
const OFFERINGS = [
  {
    key: 'BOT',
    label: 'AI Bot (screener)',
    summary:
      'Confirms the caller is a homeowner and what they need, then transfers. Under a minute.',
  },
  {
    key: 'AGENT',
    label: 'AI Agent (full qualification)',
    summary:
      'Collects service, ownership, location and project details before transferring.',
  },
] as const;

/**
 * The tabs.
 *
 * This page had grown to thirteen cards on one scroll: what the bot calls
 * itself, recording, allowances, the dialer servers, the legal entity,
 * commercial terms, products, contacts, rates and the isolation note, in the
 * order they happened to be added. Everything was there and nothing was
 * findable, and the page could not take another section without getting worse.
 *
 * Grouped by the question being asked rather than by when it was written:
 * how is this account doing, what is it running, what does it cost, and how is
 * it configured.
 */
// The Company card's fields, loaded into the draft and saved with it.
const COMPANY_FIELDS = [
  'legal_name', 'tax_id', 'registration_number', 'website', 'industry',
  'address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country',
  'contract_start', 'contract_end', 'notes',
] as const;

const TABS = ['Overview', 'Activity', 'Campaigns', 'AI Settings', 'License', 'Billing', 'Portal access', 'Settings'] as const;
type ClientTab = (typeof TABS)[number];

export default function ClientDetail() {
  // `?tab=license` opens straight on the licence -- the Licensing and
  // Invoices pages link here, and landing on Overview would be a second click.
  const [searchParams] = useSearchParams();
  const wanted = TABS.find((t) => t.toLowerCase() === (searchParams.get('tab') ?? '').toLowerCase());
  const [tab, setTab] = useState<ClientTab>(wanted ?? 'Overview');
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-client', clientId],
    queryFn: () => api.get<ClientDetailResponse>(`/admin/clients/${clientId}`),
    enabled: Boolean(clientId),
  });

  const { data: plans } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.get<{ data: Plan[] }>('/admin/plans'),
  });

  useEffect(() => {
    if (data) {
      setDraft({
        status: data.client.status,
        price_multiplier: Number(data.client.price_multiplier),
        expose_provider_costs: data.client.expose_provider_costs,
        max_concurrent_calls: data.client.max_concurrent_calls,
        plan_id: data.client.plan_id ?? '',
        billing_email: data.client.billing_email ?? '',
        offerings: data.client.offerings ?? ['BOT', 'AGENT'],
        max_bots: data.client.max_bots,
        max_client_users: data.client.max_client_users,
        max_vicidial_servers: data.client.max_vicidial_servers,
        max_vicidial_users: data.client.max_vicidial_users,
        billing_model: data.client.billing_model,
        mrc_per_bot: Number(data.client.mrc_per_bot),
        per_call_price: Number(data.client.per_call_price),
        included_calls: data.client.included_calls,
        setup_fee: Number(data.client.setup_fee),
        recording_enabled: data.client.recording_enabled,
        enforce_calling_hours: data.client.enforce_calling_hours,
        recording_retention_days: data.client.recording_retention_days,
        max_call_seconds: data.client.max_call_seconds ?? '',
        ai_company_name: data.client.ai_company_name ?? '',
        ai_company_brand: data.client.ai_company_brand ?? '',
        // THE COMPANY CARD. None of these were loaded into the draft, so the
        // card always opened blank whatever was stored -- and after a save the
        // page reloaded, blanked them again, and the edit looked lost.
        ...Object.fromEntries(
          COMPANY_FIELDS.map((key) => [key, (data.client as Record<string, unknown>)[key] ?? '']),
        ),
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...draft };
      if (payload.plan_id === '') delete payload.plan_id;
      if (payload.billing_email === '') delete payload.billing_email;
      // An empty box means "no limit of their own", which is null and not a
      // missing field: leaving it out would keep whatever limit was set
      // before, so clearing one would silently do nothing.
      payload.max_call_seconds =
        payload.max_call_seconds === '' || payload.max_call_seconds == null
          ? null
          : Number(payload.max_call_seconds);
      // An emptied box is "none", not an empty string: the server rejects ""
      // as a date, which failed the whole save.
      for (const key of COMPANY_FIELDS) {
        if (payload[key] === '') payload[key] = null;
        else if ((key === 'contract_start' || key === 'contract_end') && typeof payload[key] === 'string') {
          payload[key] = (payload[key] as string).slice(0, 10);
        }
      }
      return api.patch(`/admin/clients/${clientId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setToast({ message: 'Client updated', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not save'),
        tone: 'bad',
      }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const { client, stats, margin_30d, campaign_dispositions } = data;
  const econ = data.economics_30d;
  const marginPct =
    margin_30d.client_price > 0
      ? (1 - margin_30d.actual_cost / margin_30d.client_price) * 100
      : null;

  return (
    <div className="w-full">
      <PageHeader
        title={client.name}
        description={`${client.slug} · ${client.currency}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/clients')}>
              Back
            </Button>
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save changes
            </Button>
          </>
        }
      />

      <Tabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
      />

      {tab === 'Overview' && (
        <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Revenue (30d)"
          value={formatMoney(econ ? econ.revenue : margin_30d.client_price)}
          sub={
            econ
              ? econ.licence_revenue && econ.usage_revenue
                ? `Licence ${formatMoney(econ.licence_revenue)} + usage ${formatMoney(econ.usage_revenue)}`
                : econ.licence_revenue
                  ? 'Monthly licence — no usage billed'
                  : 'Usage billed per minute'
              : undefined
          }
        />
        <StatCard label="Provider cost (30d)" value={formatMoney(econ ? econ.provider_cost : margin_30d.actual_cost)} sub="What their calls cost you" />
        <StatCard
          label="Profit (30d)"
          value={formatMoney(econ ? econ.profit : margin_30d.gross_margin)}
          sub={
            econ
              ? econ.margin_pct !== null
                ? `${formatPercent(econ.margin_pct)} margin`
                : undefined
              : marginPct !== null
                ? formatPercent(marginPct)
                : undefined
          }
          tone={(econ ? econ.profit : margin_30d.gross_margin) >= 0 ? 'good' : 'bad'}
        />
        <StatCard label="Minutes" value={formatNumber(margin_30d.minutes)} />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4 lg:grid-cols-6">
        <StatCard label="Bots" value={formatNumber(stats.bots)} />
        <StatCard label="Groups" value={formatNumber(stats.bot_groups)} />
        <StatCard label="Users" value={formatNumber(stats.users)} />
        <StatCard label="Agents" value={formatNumber(stats.vicidial_users)} />
        <StatCard label="Assignments" value={formatNumber(stats.assignments)} />
        <StatCard label="Calls" value={formatNumber(stats.calls)} />
      </div>

      <div className="mt-6">
        <CampaignDispositions
          data={campaign_dispositions}
          description="What this client's bots wrote back to the dialer, per campaign · last 30 days"
        />
      </div>

      <ClientContacts tenantId={clientId!} />
        </>
      )}

      {tab === 'Campaigns' && (
        <>
      <div className="mt-4">
        <VicidialServers tenantId={client.id} />
      </div>

      <ClientProducts
        tenantId={clientId!}
        clientName={client.name}
        enabled={Boolean((client.features ?? {}).soundboard)}
        onToast={setToast}
      />

        </>
      )}

      {tab === 'Activity' && <ClientActivity tenantId={clientId!} />}

      {tab === 'AI Settings' && <ClientAiSettings tenantId={clientId!} />}

      {tab === 'License' && <ClientLicense clientId={clientId!} />}

      {tab === 'Portal access' && <ClientPortalAccess clientId={clientId!} />}

      {tab === 'Billing' && (
        <>
      {/* Allowances and pricing. These live on the client, not the plan, so two
          clients on one plan can be on different terms. */}
      <Card
        title="Allowances and pricing"
        description="What this client may run, and how they are charged for it."
        className="mt-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="AI products"
            hint="Which flows this client may set a bot to. Refused on write, not just hidden: a client cannot select one they were not sold."
          >
            <div className="space-y-2">
              {OFFERINGS.map((offering) => {
                const selected = (draft.offerings ?? ['BOT', 'AGENT']) as string[];
                const on = selected.includes(offering.key);
                return (
                  <label key={offering.key} className="flex gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={on}
                      onChange={(e) =>
                        setDraft((d) => {
                          const current = ((d.offerings ?? ['BOT', 'AGENT']) as string[]).filter(
                            (k) => k !== offering.key,
                          );
                          const next = e.target.checked
                            ? [...current, offering.key]
                            : current;
                          // Never empty: a client entitled to nothing could not
                          // run a bot at all, and the page would give no clue why.
                          return { ...d, offerings: next.length ? next : [offering.key] };
                        })
                      }
                    />
                    <span>
                      <span className="block font-medium">{offering.label}</span>
                      <span className="block text-xs text-ink-500 dark:text-ink-400">
                        {offering.summary}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field
            label="Bot allowance"
            hint={`Currently using ${stats.bots}. Creating a bot beyond this is refused by the database, not just the UI.`}
          >
            <Input
              type="number"
              min={0}
              value={Number(draft.max_bots ?? 10)}
              onChange={(e) => setDraft((d) => ({ ...d, max_bots: Number(e.target.value) }))}
            />
          </Field>

          <Field
            label="Vicidial server allowance"
            hint={`Currently ${stats.vicidial_servers ?? 0} configured.`}
          >
            <Input
              type="number"
              min={0}
              value={Number(draft.max_vicidial_servers ?? 5)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max_vicidial_servers: Number(e.target.value) }))
              }
            />
          </Field>

          <Field
            label="Vicidial agent allowance"
            hint={`Currently ${stats.vicidial_users}, across all servers.`}
          >
            <Input
              type="number"
              min={0}
              value={Number(draft.max_vicidial_users ?? 50)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max_vicidial_users: Number(e.target.value) }))
              }
            />
          </Field>

          <Field label="Portal user allowance" hint={`Currently ${stats.users}.`}>
            <Input
              type="number"
              min={0}
              value={Number(draft.max_client_users ?? 10)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max_client_users: Number(e.target.value) }))
              }
            />
          </Field>

          <Field
            label="Billing model"
            hint="Overrides the plan. “Cost × multiplier” means no per-client override."
          >
            <Select
              value={String(draft.billing_model ?? 'MULTIPLIER')}
              onChange={(e) => setDraft((d) => ({ ...d, billing_model: e.target.value }))}
            >
              <option value="MULTIPLIER">Cost × multiplier</option>
              <option value="MRC_PER_BOT">Monthly rate per bot</option>
              <option value="PER_CALL">Per call</option>
              <option value="HYBRID">Monthly per bot + per call</option>
            </Select>
          </Field>

          <Field label="One-time setup fee" hint="Charged on the first invoice only.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={Number(draft.setup_fee ?? 0)}
              onChange={(e) => setDraft((d) => ({ ...d, setup_fee: Number(e.target.value) }))}
            />
          </Field>

          {/* Only the fields the chosen model actually uses. Showing all four
              invites someone to fill in a number that is never charged. */}
          {(draft.billing_model === 'MRC_PER_BOT' || draft.billing_model === 'HYBRID') && (
            <Field
              label="Monthly rate per bot"
              hint={`× ${stats.bots} active bot(s) = ${formatMoney(
                Number(draft.mrc_per_bot ?? 0) * stats.bots,
              )} per month.`}
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                value={Number(draft.mrc_per_bot ?? 0)}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, mrc_per_bot: Number(e.target.value) }))
                }
              />
            </Field>
          )}

          {(draft.billing_model === 'PER_CALL' || draft.billing_model === 'HYBRID') && (
            <>
              <Field label="Price per call">
                <Input
                  type="number"
                  min={0}
                  step="0.0001"
                  value={Number(draft.per_call_price ?? 0)}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, per_call_price: Number(e.target.value) }))
                  }
                />
              </Field>
              <Field
                label="Calls included"
                hint="Consumed before the per-call price starts applying, each period."
              >
                <Input
                  type="number"
                  min={0}
                  value={Number(draft.included_calls ?? 0)}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, included_calls: Number(e.target.value) }))
                  }
                />
              </Field>
            </>
          )}
        </div>
      </Card>

      <Card title="Commercial settings" className="mt-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Status">
            <Select
              value={String(draft.status ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
            >
              {['ACTIVE', 'TRIAL', 'SUSPENDED', 'CANCELLED'].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Plan">
            <Select
              value={String(draft.plan_id ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, plan_id: e.target.value }))}
            >
              <option value="">No plan</option>
              {(plans?.data ?? []).map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Price multiplier"
            hint="CLIENT PRICE = ACTUAL COST × this. Applies to new usage; existing rows keep the multiplier they were billed at."
          >
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={Number(draft.price_multiplier ?? 2.2)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, price_multiplier: Number(e.target.value) }))
              }
            />
          </Field>

          <Field label="Max concurrent calls">
            <Input
              type="number"
              min={1}
              value={Number(draft.max_concurrent_calls ?? 50)}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max_concurrent_calls: Number(e.target.value) }))
              }
            />
          </Field>

          <Field
            label="Max call length (seconds)"
            hint="Applies to AI Bots and Soundboard Bots alike. Leave empty to use the bot's own limit."
          >
            <Input
              type="number"
              min={30}
              max={3600}
              placeholder="bot default"
              value={String(draft.max_call_seconds ?? '')}
              onChange={(e) =>
                setDraft((d) => ({ ...d, max_call_seconds: e.target.value }))
              }
            />
          </Field>

          <Field label="Billing email">
            <Input
              type="email"
              value={String(draft.billing_email ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, billing_email: e.target.value }))}
            />
          </Field>
        </div>

        <label className="mt-4 flex items-start gap-2 text-sm text-ink-700 dark:text-ink-200">
          <input
            type="checkbox"
            checked={Boolean(draft.expose_provider_costs)}
            onChange={(e) =>
              setDraft((d) => ({ ...d, expose_provider_costs: e.target.checked }))
            }
            className="mt-0.5 rounded border-ink-300"
          />
          <span>
            <span className="font-medium">Show provider costs to this client</span>
            <span className="block text-xs text-ink-500 dark:text-ink-400">
              Off by default. With this on, the client can see your actual provider cost and
              therefore infer your markup — turn it on only for cost-plus arrangements.
            </span>
          </span>
        </label>

        <div className="mt-6 border-t border-ink-200 pt-4 dark:border-ink-800">
          <h3 className="text-sm font-semibold">How this client is billed</h3>
          <p className="mt-1 text-xs text-ink-500">
            The model decides which of the numbers below apply. The rest are
            hidden rather than ignored — a per-bot price left over from a
            different arrangement is how a client gets billed for something
            nobody sold them.
          </p>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field label="Billing model">
              <Select
                value={String(draft.billing_model ?? 'MULTIPLIER')}
                onChange={(e) => setDraft((d) => ({ ...d, billing_model: e.target.value }))}
              >
                <option value="MULTIPLIER">Cost plus (usage × multiplier)</option>
                <option value="MRC_PER_BOT">Per bot, monthly</option>
                <option value="PER_CALL">Per call</option>
                <option value="PER_MINUTE">Per minute</option>
                <option value="HYBRID">Per bot + usage</option>
              </Select>
            </Field>

            {['MRC_PER_BOT', 'HYBRID'].includes(String(draft.billing_model)) && (
              <Field label="Price per bot, per month">
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={Number(draft.mrc_per_bot ?? 0)}
                  onChange={(e) => setDraft((d) => ({ ...d, mrc_per_bot: Number(e.target.value) }))}
                />
              </Field>
            )}

            {['PER_CALL', 'HYBRID'].includes(String(draft.billing_model)) && (
              <>
                <Field label="Price per call">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={Number(draft.per_call_price ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, per_call_price: Number(e.target.value) }))
                    }
                  />
                </Field>
                <Field label="Calls included">
                  <Input
                    type="number"
                    min="0"
                    value={Number(draft.included_calls ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, included_calls: Number(e.target.value) }))
                    }
                  />
                </Field>
              </>
            )}

            {['PER_MINUTE', 'MRC_PER_BOT', 'HYBRID'].includes(String(draft.billing_model)) && (
              <>
                <Field
                  label="Price per minute"
                  hint="Replaces cost-plus: the client's bill does not move when a vendor changes a rate."
                >
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={Number(draft.per_minute_price ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, per_minute_price: Number(e.target.value) }))
                    }
                  />
                </Field>
                <Field label="Minutes included per bot">
                  <Input
                    type="number"
                    min="0"
                    value={Number(draft.included_minutes_per_bot ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        included_minutes_per_bot: Number(e.target.value),
                      }))
                    }
                  />
                </Field>
                <Field label="Minutes included (flat)">
                  <Input
                    type="number"
                    min="0"
                    value={Number(draft.included_minutes ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, included_minutes: Number(e.target.value) }))
                    }
                  />
                </Field>
                <Field
                  label="Overage per minute"
                  hint="Left at zero, minutes past the allowance are charged at the per-minute price."
                >
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={Number(draft.overage_per_minute ?? 0)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, overage_per_minute: Number(e.target.value) }))
                    }
                  />
                </Field>
              </>
            )}

            <Field label="One-off setup fee">
              <Input
                type="number"
                step="1"
                min="0"
                value={Number(draft.setup_fee ?? 0)}
                onChange={(e) => setDraft((d) => ({ ...d, setup_fee: Number(e.target.value) }))}
              />
            </Field>
          </div>

          {/* The same numbers, as a sentence. Terms assembled from six fields
              are got wrong in both directions, and this is where that gets
              noticed rather than on an invoice. */}
          <p className="mt-4 rounded-md bg-ink-50 p-3 text-sm dark:bg-ink-950/50">
            <strong>This client will be billed:</strong> {describeBilling(draft)}
          </p>
        </div>

      </Card>

      <ClientRates tenantId={clientId!} />
        </>
      )}

      {tab === 'Settings' && (
        <>
      {/* What the bot says it is. One premade engine serves every client; this
          is the field that makes it theirs. */}
      <Card
        title="What the bot says it is"
        description="Fills the {{company}} placeholder in whichever engine this client's bots run."
        className="mt-6"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Company name"
            hint="Spoken on every call: “this is the … automated assistant”."
          >
            <Input
              value={String(draft.ai_company_name ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, ai_company_name: e.target.value }))}
              placeholder={client.name}
            />
          </Field>
          <Field
            label="Brand (optional)"
            hint="Only set this when the trading name differs from the company name."
          >
            <Input
              value={String(draft.ai_company_brand ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, ai_company_brand: e.target.value }))}
            />
          </Field>
        </div>
        <p className="mt-3 rounded-lg border border-line bg-sunken px-3 py-2 text-xs text-ink-600 dark:text-ink-300">
          Preview:{' '}
          <span className="italic">
            “Hi, this is the{' '}
            <strong className="not-italic">
              {String(draft.ai_company_name || client.name)}
            </strong>{' '}
            automated assistant…”
          </span>
        </p>
      </Card>

      <Card
        title="Calling hours"
        description="What happens when the dialer calls outside permitted hours."
        className="mt-6"
      >
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={draft.enforce_calling_hours !== false}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                enforce_calling_hours: e.target.checked,
              }))
            }
            className="mt-1 rounded border-ink-300"
          />
          <span className="text-sm text-ink-800 dark:text-ink-100">
            Refuse calls outside permitted hours
            <span className="mt-0.5 block text-xs text-ink-500 dark:text-ink-400">
              8am to 9pm in the <strong>called party's</strong> own time, worked
              out from the lead's state or area code. With this on, the bot ends
              the call without speaking and dispositions it for a retry.
            </span>
          </span>
        </label>

        {/* The distinction that matters, and the reason the log is not part of
            the switch: turning this off changes what Glee DOES, never what it
            records. An operator who switched it off still has to be able to
            answer "did we call this person at one in the morning" six weeks
            later. */}
        {draft.enforce_calling_hours === false ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            Out-of-hours calls will be <strong>answered</strong>. They are still
            logged, every one, so the record survives this setting.
            <span className="mt-1.5 block">
              Glee does not place calls — the dialer does. Turning this off does
              not stop the calls happening; it stops Glee refusing them. The
              setting that prevents them is the campaign's own call-time
              configuration in Vicidial.
            </span>
          </div>
        ) : null}
      </Card>

      <Card
        title="Call recording"
        description="Off unless this client asks for it."
        className="mt-6"
      >
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={Boolean(draft.recording_enabled)}
            onChange={(e) =>
              setDraft((d) => ({ ...d, recording_enabled: e.target.checked }))
            }
            className="mt-1 rounded border-ink-300"
          />
          <span className="text-sm text-ink-800 dark:text-ink-100">
            Record this client's calls
            <span className="mt-0.5 block text-xs text-ink-500 dark:text-ink-400">
              Recordings appear in the client's own portal, on each call, and can
              be played there.
            </span>
          </span>
        </label>

        {/* Not legal advice, and not a warning that can be dismissed by
            ticking the box faster. Consent rules genuinely differ by state and
            by country, and this is the moment somebody is deciding. */}
        {draft.recording_enabled ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            Whether a call may be recorded, and whether every party has to be
            told, depends on where the caller and the customer are. Several US
            states require all-party consent. Confirm this client's campaigns
            announce it where their jurisdiction requires it — the platform
            cannot determine that for them.
          </div>
        ) : null}

        <div className="mt-4 max-w-xs">
          <Field
            label="Keep recordings for"
            hint="The nightly cleanup deletes the audio after this many days."
          >
            <Input
              type="number"
              min={1}
              max={3650}
              value={String(draft.recording_retention_days ?? 30)}
              disabled={!draft.recording_enabled}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  recording_retention_days: Number(e.target.value),
                }))
              }
            />
          </Field>
        </div>
      </Card>

      <Card
        title="Company"
        description="The legal entity, for invoices and for knowing who this is."
        className="mt-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              ['legal_name', 'Legal name'],
              ['tax_id', 'Tax / VAT id'],
              ['registration_number', 'Company number'],
              ['website', 'Website'],
              ['industry', 'Industry'],
              ['address_line1', 'Address'],
              ['address_line2', 'Address line 2'],
              ['city', 'City'],
              ['state', 'State / region'],
              ['postal_code', 'Postal code'],
              ['country', 'Country'],
            ] as [string, string][]
          ).map(([key, label]) => (
            <Field key={key} label={label}>
              <Input
                value={String(draft[key] ?? '')}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            </Field>
          ))}
          <Field label="Contract starts">
            <Input
              type="date"
              value={String(draft.contract_start ?? '').slice(0, 10)}
              onChange={(e) => setDraft((d) => ({ ...d, contract_start: e.target.value }))}
            />
          </Field>
          <Field label="Contract ends" hint="Renewal date, for the diary.">
            <Input
              type="date"
              value={String(draft.contract_end ?? '').slice(0, 10)}
              onChange={(e) => setDraft((d) => ({ ...d, contract_end: e.target.value }))}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Notes">
            <Textarea
              rows={3}
              value={String(draft.notes ?? '')}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setDraft((d) => ({ ...d, notes: e.target.value }))
              }
              placeholder="Anything the next person to pick this account up should know."
            />
          </Field>
        </div>
      </Card>

      <Card title="Tenant isolation" className="mt-4">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          This client&apos;s users can only ever see this tenant&apos;s data. The boundary is
          enforced in three places: the token, the service layer, and Postgres row-level
          security — the application connects as a role that cannot bypass RLS, so a missing
          tenant predicate in code still cannot leak another tenant&apos;s rows.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge tone="green">JWT tenant binding</Badge>
          <Badge tone="green">Service-layer scoping</Badge>
          <Badge tone="green">Postgres RLS</Badge>
        </div>
      </Card>

        </>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

interface RateOverrideRow {
  usage_kind: string;
  unit_cost: string | number | null;
  client_unit_price: string | number | null;
  unit: string | null;
  note: string | null;
  updated_at: string | null;
}

interface RatesResponse {
  data: RateOverrideRow[];
  platform_rates: Record<string, { unit_cost: number; unit: string | null }>;
  price_multiplier: number;
}

/** The kinds worth pricing per client, in the order they matter. */
const RATE_KINDS: [string, string, string][] = [
  ['TELEPHONY_MINUTES', 'Telephony', 'per minute'],
  ['STT_SECONDS', 'Speech recognition', 'per second'],
  ['TTS_CHARACTERS', 'Speech synthesis', 'per character'],
  ['LLM_INPUT_TOKENS', 'AI input', 'per token'],
  ['LLM_OUTPUT_TOKENS', 'AI output', 'per token'],
];

/**
 * What this client costs and what they are charged, where it differs.
 *
 * Telephony is why this exists. A client dialling over their own carrier out
 * of their own Vicidial costs this platform nothing for minutes, and charging
 * them for it is inventing a line item; another client on another carrier
 * costs something else again. One rate card cannot be true of all of them.
 */
/**
 * Which products this client has, and their Soundboard Bots.
 *
 * Soundboard access is not part of the draft saved by the Save button above.
 * It writes through its own endpoint, which merges one key into `features`
 * rather than replacing the object — `features` is open by design, one
 * client's entitlements are decided one at a time, and a wholesale write would
 * take away whatever else somebody had switched on from another screen.
 *
 * The boards are listed here, read-only, because the mistake this page exists
 * to prevent is the silent one: a board built for a client who cannot see the
 * product. The board runs, their page says Soundboard Bots are not on their
 * account, and nothing anywhere connects the two.
 */
function ClientProducts({
  tenantId,
  clientName,
  enabled,
  onToast,
}: {
  tenantId: string;
  clientName: string;
  enabled: boolean;
  onToast: (t: { message: string; tone: 'good' | 'bad' }) => void;
}) {
  const queryClient = useQueryClient();

  const boards = useQuery({
    queryKey: ['admin-soundboards'],
    queryFn: () => api.get<{ data: AdminBoard[] }>('/admin/soundboards'),
  });

  const access = useMutation({
    mutationFn: (next: boolean) =>
      api.put(`/admin/clients/${tenantId}/soundboard-access`, { enabled: next }),
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey: ['admin-client', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['admin-soundboards'] });
      onToast({
        message: next
          ? 'Soundboard Bots are now on this account.'
          : 'Soundboard Bots hidden from this client.',
        tone: 'good',
      });
    },
    onError: (err) => onToast({ message: errorMessage(err, 'Could not change'), tone: 'bad' }),
  });

  const mine = (boards.data?.data ?? []).filter((b) => b.tenant_id === tenantId);

  return (
    <Card
      title="Products"
      description="What this client may use, beyond their AI Bots."
      className="mt-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Soundboard Bots</div>
          <p className="mt-0.5 text-sm text-ink-600 dark:text-ink-300">
            Recorded screening calls. No speech synthesis and no model — the call
            costs what speech recognition costs. The client sees that a board
            exists and the calls it made, and nothing else to configure.
          </p>
        </div>
        <Button
          variant={enabled ? 'secondary' : 'primary'}
          loading={access.isPending}
          onClick={() => access.mutate(!enabled)}
        >
          {enabled ? 'Turn off' : 'Turn on'}
        </Button>
      </div>

      {mine.length > 0 ? (
        <div className="mt-4 divide-y divide-ink-100 border-t border-ink-100 dark:divide-ink-800/70 dark:border-ink-800/70">
          {mine.map((board) => (
            <div key={board.id} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm">
                  {board.name} <span className="text-xs text-ink-500">{board.code}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                  {board.service && <span>{board.service}</span>}
                  <span>
                    {board.clips} clip{board.clips === 1 ? '' : 's'}
                  </span>
                  {board.playable ? (
                    <Badge tone="green">playable</Badge>
                  ) : (
                    <Badge tone="red">no question clip</Badge>
                  )}
                </div>
              </div>
              <Link
                to="/soundboards"
                className="text-xs text-brand-600 underline dark:text-brand-300"
              >
                Clips
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 border-t border-ink-100 pt-4 text-sm text-ink-500 dark:border-ink-800/70">
          {clientName} has no soundboards yet.{' '}
          <Link to="/soundboards" className="text-brand-600 underline dark:text-brand-300">
            Create one
          </Link>
          .
        </p>
      )}

      {enabled && mine.length === 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          The product is switched on but there is nothing to show them, so their
          Soundboard Bots page will be empty.
        </p>
      )}
      {!enabled && mine.length > 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          These boards will still run and still transfer. The client simply has
          no page for them.
        </p>
      )}
    </Card>
  );
}

function ClientRates({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, { cost: string; price: string; note: string }>>(
    {},
  );
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const rates = useQuery({
    queryKey: ['client-rates', tenantId],
    queryFn: () => api.get<RatesResponse>(`/admin/clients/${tenantId}/rates`),
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put(`/admin/clients/${tenantId}/rates`, body),
    onSuccess: () => {
      setToast({ message: 'Rate saved. It applies to calls in progress.', tone: 'good' });
      queryClient.invalidateQueries({ queryKey: ['client-rates', tenantId] });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  const clear = useMutation({
    mutationFn: (kind: string) =>
      api.delete(`/admin/clients/${tenantId}/rates/${kind}`),
    onSuccess: () => {
      setToast({ message: 'Back on the platform rate.', tone: 'good' });
      queryClient.invalidateQueries({ queryKey: ['client-rates', tenantId] });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not clear'), tone: 'bad' }),
  });

  const existing = new Map((rates.data?.data ?? []).map((row) => [row.usage_kind, row]));

  const field = (kind: string, which: 'cost' | 'price' | 'note') => {
    const row = existing.get(kind);
    const pending = draft[kind];
    if (pending) return pending[which];
    if (which === 'note') return row?.note ?? '';
    const value = which === 'cost' ? row?.unit_cost : row?.client_unit_price;
    return value === null || value === undefined ? '' : String(value);
  };

  const edit = (kind: string, which: 'cost' | 'price' | 'note', value: string) =>
    setDraft((d) => {
      // Seeded from what is saved the first time a row is touched, so editing
      // one field does not blank the other two. Written as a fallback rather
      // than a spread over the same keys: that read as a mistake, and the
      // compiler said so.
      const current = d[kind] ?? {
        cost: field(kind, 'cost'),
        price: field(kind, 'price'),
        note: field(kind, 'note'),
      };
      return { ...d, [kind]: { ...current, [which]: value } };
    });

  return (
    <Card
      title="This client's rates"
      description="Where what they cost, or what they are charged, is not the platform's usual answer."
      className="mt-4"
    >
      <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
        Leave both empty to use the platform rate and this client&apos;s multiplier
        (&times;{rates.data?.price_multiplier ?? '—'}). <strong>Cost</strong> is what
        we pay — zero for a client on their own SIP trunk. <strong>Charged</strong> is
        what they pay per unit and replaces the multiplier entirely; zero there means
        they are not billed for it at all.
      </p>

      {rates.isPending ? (
        <Loading label="Reading rates…" />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200 text-sm dark:divide-ink-800">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="px-2 py-2">Usage</th>
                <th className="px-2 py-2">Platform</th>
                <th className="px-2 py-2">Cost</th>
                <th className="px-2 py-2">Charged</th>
                <th className="px-2 py-2">Why</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 dark:divide-ink-800/70">
              {RATE_KINDS.map(([kind, label, unit]) => {
                const platform = rates.data?.platform_rates?.[kind];
                const overridden = existing.has(kind);
                return (
                  <tr key={kind}>
                    <td className="px-2 py-2">
                      <div>{label}</div>
                      <div className="text-xs text-ink-500">{unit}</div>
                    </td>
                    <td className="px-2 py-2 text-xs tabular-nums text-ink-500">
                      {platform ? platform.unit_cost.toFixed(8) : '—'}
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={field(kind, 'cost')}
                        onChange={(e) => edit(kind, 'cost', e.target.value)}
                        placeholder="platform"
                        className="w-28"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={field(kind, 'price')}
                        onChange={(e) => edit(kind, 'price', e.target.value)}
                        placeholder="multiplier"
                        className="w-28"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={field(kind, 'note')}
                        onChange={(e) => edit(kind, 'note', e.target.value)}
                        placeholder="their own trunk"
                        className="w-40"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={save.isPending}
                          onClick={() => {
                            const cost = field(kind, 'cost').trim();
                            const price = field(kind, 'price').trim();
                            save.mutate({
                              usage_kind: kind,
                              unit_cost: cost === '' ? null : Number(cost),
                              client_unit_price: price === '' ? null : Number(price),
                              unit: unit.replace('per ', ''),
                              note: field(kind, 'note') || null,
                            });
                          }}
                        >
                          Save
                        </Button>
                        {overridden && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => clear.mutate(kind)}
                          >
                            Use platform rate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}

interface Contact {
  id: string;
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  notes: string | null;
}

const CONTACT_ROLES = ['PRIMARY', 'BILLING', 'TECHNICAL', 'ESCALATION', 'OTHER'];

/**
 * Who to call, which is rarely one person.
 *
 * Billing goes to accounts payable, an outage calls the technical contact,
 * and the person who signed is usually neither.
 */
function ClientContacts({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ role: 'PRIMARY', name: '', email: '', phone: '', title: '' });
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const contacts = useQuery({
    queryKey: ['client-contacts', tenantId],
    queryFn: () => api.get<{ data: Contact[] }>(`/admin/clients/${tenantId}/contacts`),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['client-contacts', tenantId] });

  const add = useMutation({
    mutationFn: () =>
      api.post(`/admin/clients/${tenantId}/contacts`, {
        role: form.role,
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        title: form.title || null,
      }),
    onSuccess: () => {
      setForm({ role: 'PRIMARY', name: '', email: '', phone: '', title: '' });
      setAdding(false);
      refresh();
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not add'), tone: 'bad' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/clients/${tenantId}/contacts/${id}`),
    onSuccess: refresh,
    onError: (err) => setToast({ message: errorMessage(err, 'Could not remove'), tone: 'bad' }),
  });

  return (
    <Card
      title="Contacts"
      description="Billing goes to accounts payable; an outage calls somebody else."
      className="mt-4"
      actions={
        <Button variant="secondary" size="sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add contact'}
        </Button>
      }
    >
      {adding && (
        <div className="mb-4 grid gap-3 rounded-md border border-ink-200 p-3 sm:grid-cols-2 dark:border-ink-800">
          <Field label="Role">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {CONTACT_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.charAt(0) + role.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <div className="flex items-end">
            <Button loading={add.isPending} disabled={!form.name.trim()} onClick={() => add.mutate()}>
              Save contact
            </Button>
          </div>
        </div>
      )}

      {contacts.isPending ? (
        <Loading label="Reading contacts…" />
      ) : !contacts.data?.data.length ? (
        <p className="text-sm text-ink-500">
          No contacts yet. The billing email on the account is all there is to go on.
        </p>
      ) : (
        <div className="divide-y divide-ink-100 dark:divide-ink-800/70">
          {contacts.data.data.map((contact) => (
            <div key={contact.id} className="flex items-start justify-between py-2">
              <div>
                <div className="text-sm font-medium">
                  {contact.name}{' '}
                  <Badge tone="gray">{contact.role.toLowerCase()}</Badge>
                </div>
                <div className="text-xs text-ink-500">
                  {[contact.title, contact.email, contact.phone].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove.mutate(contact.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}

/**
 * What this client will actually be billed, in words.
 *
 * Built from the same values the form writes, so it cannot describe an
 * arrangement other than the one being saved -- which is the only way a
 * summary is worth anything.
 */
function describeBilling(draft: Record<string, unknown>): string {
  const model = String(draft.billing_model ?? 'MULTIPLIER');
  const money = (value: unknown) => Number(value ?? 0);
  const parts: string[] = [];

  if (model === 'MULTIPLIER') {
    parts.push(`what their usage costs us, times ${money(draft.price_multiplier) || 2.2}`);
  }
  if (['MRC_PER_BOT', 'HYBRID'].includes(model) && money(draft.mrc_per_bot) > 0) {
    parts.push(`${formatMoney(money(draft.mrc_per_bot))} per active bot per month`);
  }
  if (['PER_CALL', 'HYBRID'].includes(model) && money(draft.per_call_price) > 0) {
    const included = money(draft.included_calls);
    parts.push(
      `${formatMoney(money(draft.per_call_price))} per call` +
        (included ? ` after ${included} included` : ''),
    );
  }
  if (['PER_MINUTE', 'MRC_PER_BOT', 'HYBRID'].includes(model)) {
    const perBot = money(draft.included_minutes_per_bot);
    const flat = money(draft.included_minutes);
    const rate = money(draft.overage_per_minute) || money(draft.per_minute_price);
    const allowance: string[] = [];
    if (perBot) allowance.push(`${perBot} minutes per bot`);
    if (flat) allowance.push(`${flat} minutes`);
    if (rate > 0) {
      parts.push(
        allowance.length
          ? `${allowance.join(' plus ')} included, then ${formatMoney(rate)} a minute`
          : `${formatMoney(rate)} a minute`,
      );
    } else if (allowance.length) {
      parts.push(`${allowance.join(' plus ')} included, and nothing beyond them`);
    }
  }
  if (money(draft.setup_fee) > 0) {
    parts.push(`${formatMoney(money(draft.setup_fee))} once, to set up`);
  }

  if (!parts.length) {
    return 'nothing — no price is set on any of the fields this model uses.';
  }
  return parts.join('; ') + '.';
}
